import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import firebaseApp, { db } from '../firebase';
import ProgressBar from '../components/ProgressBar';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '../components/Dashboardv2/ui/sidebar';
import { cn } from '../components/Dashboard/ui/utils';
import {
  BarChart3,
  Users,
  FileText,
  MapPin,
  Calendar,
  FolderOpen,
  Trees,
  MessageSquare,
  HelpCircle,
  PieChart,
  Megaphone
} from "lucide-react";
import { OverviewTab } from '../components/Dashboard/OverviewTab';
import { PlanDetailsTab } from '../components/Dashboard/PlanDetailsTab';
import { ProgressTab } from '../components/Dashboard/ProgressTab';
import { SurveysTab } from '../components/Dashboard/SurveysTab';
import { SurveyResultsTab } from '../components/Dashboard/SurveyResultsTab';
import { VisualsTab } from '../components/Dashboard/VisualsTab';
import { NewsTab } from '../components/Dashboard/NewsTab';
import { MilestonesTab } from '../components/Dashboard/MilestonesTab';
import { FAQTab } from '../components/Dashboard/FAQTab';
import { ResourcesTab } from '../components/Dashboard/Resources';
import { FinalFeedbackTab } from '../components/Dashboard/FinalFeedbackTab';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  increment,
  query,
  orderBy,
  setDoc
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

function DashboardContent() {
  const { setOpenMobile, isMobile } = useSidebar();
  const location = useLocation();
  const STAGES = ['IDEAS', 'RESEARCH', 'PLANNING', 'SUBMITTING', 'IMPLEMENTATION', 'COMPLETE'];

  // Define tabs for Miner's Park Masterplan sections
  const tabs = [
    { id: "overview", label: "Overview", icon: Trees },
    { id: "plan-details", label: "Plan Details", icon: MapPin },
    { id: "progress", label: "Progress", icon: BarChart3 },
    { id: "milestones", label: "Milestones", icon: FolderOpen },
    { id: "surveys", label: "Surveys", icon: MessageSquare },
    // { id: "survey-results", label: "Survey Results", icon: PieChart },
    { id: "visuals", label: "Beaconsfield Data", icon: Users },
    { id: "resources", label: "Resources", icon: Calendar },
    { id: "news", label: "News", icon: FileText },
    { id: "final-feedback", label: "Final Feedback", icon: Megaphone },
    { id: "faq", label: "FAQ", icon: HelpCircle }
  ];

  // State management
  const [activeTab, setActiveTab] = useState("overview");
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [newPost, setNewPost] = useState({ title: '', content: '', image: '' });
  const [editingPost, setEditingPost] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [postError, setPostError] = useState(null);
  const [posting, setPosting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newsStartIndex, setNewsStartIndex] = useState(0);
  const [supporters, setSupporters] = useState(0);
  const [hasSupported, setHasSupported] = useState(false);
  const [supporting, setSupporting] = useState(false);
  const [showThanks, setShowThanks] = useState(false);
  const [progressIndex, setProgressIndex] = useState(0);
  const [progressDetails, setProgressDetails] = useState(Array(STAGES.length).fill(''));
  const [progressImages, setProgressImages] = useState(Array(STAGES.length).fill(''));
  const [sessionVotes, setSessionVotes] = useState({});
  const [sessionFeedbackAgrees, setSessionFeedbackAgrees] = useState({});
  const [replyInputs, setReplyInputs] = useState({});
  const [replyLoading, setReplyLoading] = useState({});

  const postsPerPage = 4;
  const latestNews = posts.slice(newsStartIndex, newsStartIndex + postsPerPage);
  const canScrollLeft = newsStartIndex > 0;
  const canScrollRight = newsStartIndex + postsPerPage < posts.length;
  const popularConcerns = feedbackList.slice(0, 5);

  // Check for tab query param
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabExists = tabs.find(t => t.id === tabParam);
      if (tabExists) {
        setActiveTab(tabParam);
      }
    }
  }, [location.search]);

  // Load session data
  useEffect(() => {
    const storedVotes = sessionStorage.getItem('dashboardVotes');
    if (storedVotes) setSessionVotes(JSON.parse(storedVotes));

    const storedAgrees = sessionStorage.getItem('dashboardFeedbackAgrees');
    if (storedAgrees) setSessionFeedbackAgrees(JSON.parse(storedAgrees));

    const storedSupported = sessionStorage.getItem('dashboardHasSupported');
    setHasSupported(storedSupported === '1');
  }, []);

  // Save session data
  useEffect(() => {
    sessionStorage.setItem('dashboardVotes', JSON.stringify(sessionVotes));
  }, [sessionVotes]);

  useEffect(() => {
    sessionStorage.setItem('dashboardFeedbackAgrees', JSON.stringify(sessionFeedbackAgrees));
  }, [sessionFeedbackAgrees]);

  useEffect(() => {
    sessionStorage.setItem('dashboardHasSupported', hasSupported ? '1' : '0');
  }, [hasSupported]);

  // Auth state
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  // Firestore listeners
  useEffect(() => {
    const unsubPosts = onSnapshot(
      query(collection(db, 'posts'), orderBy('time', 'desc')),
      (snapshot) => {
        setPosts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      }
    );

    const unsubFeedback = onSnapshot(
      query(collection(db, 'feedback'), orderBy('time', 'desc')),
      (snapshot) => {
        setFeedbackList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    );

    const unsubProgress = onSnapshot(
      doc(db, 'settings', 'progress'),
      (snapshot) => {
        const data = snapshot.data();
        if (data) {
          const incomingDetails = Array.isArray(data.details) ? data.details : [];
          const incomingImages = Array.isArray(data.images) ? data.images : [];
          const padTo = STAGES.length;
          const paddedDetails = [...incomingDetails, ...Array(Math.max(0, padTo - incomingDetails.length)).fill('')].slice(0, padTo);
          const paddedImages = [...incomingImages, ...Array(Math.max(0, padTo - incomingImages.length)).fill('')].slice(0, padTo);
          const legacy = incomingDetails.length > 0 && incomingDetails.length < padTo;
          const idx = data.index ?? 0;
          setProgressIndex(legacy ? Math.min(idx + 1, padTo - 1) : idx);
          setProgressDetails(paddedDetails);
          setProgressImages(paddedImages);
          setSupporters(typeof data.supporters === 'number' ? data.supporters : 0);
        }
      }
    );

    return () => {
      unsubPosts();
      unsubFeedback();
      unsubProgress();
    };
  }, []);

  // Handler functions
  const handleDeletePost = async (postId) => {
    if (!user) return;
    const confirmDelete = window.confirm('Delete this post? This cannot be undone.');
    if (!confirmDelete) return;
    const postRef = doc(db, 'posts', postId);
    await deleteDoc(postRef);
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!user) return;
    const confirmDelete = window.confirm('Delete this concern/idea? This cannot be undone.');
    if (!confirmDelete) return;
    const fbRef = doc(db, 'feedback', feedbackId);
    await deleteDoc(fbRef);
  };

  const handleSaveProgress = async (nextDetails = progressDetails, nextImages = progressImages, nextIndex = progressIndex) => {
    const ref = doc(db, 'settings', 'progress');
    try {
      await updateDoc(ref, { index: nextIndex, details: nextDetails, images: nextImages });
    } catch (e) {
      await setDoc(ref, { index: nextIndex, details: nextDetails, images: nextImages }, { merge: true });
    }
  };

  const handleSupport = async () => {
    if (hasSupported || supporting) return;
    setSupporting(true);
    const ref = doc(db, 'settings', 'progress');
    try {
      await updateDoc(ref, { supporters: increment(1) });
      setHasSupported(true);
      setShowThanks(true);
    } catch (e) {
      try {
        await setDoc(ref, { supporters: 1 }, { merge: true });
        setHasSupported(true);
        setShowThanks(true);
      } catch { }
    } finally {
      setSupporting(false);
    }
  };

  const handleProgressIndexChange = (newIndex) => {
    setProgressIndex(newIndex);
    handleSaveProgress(progressDetails, progressImages, newIndex);
  };

  const handleStageImageFile = async (stageIndex, file) => {
    if (!file) return;
    try {
      const storage = getStorage();
      const fileRef = storageRef(storage, `progress/${STAGES[stageIndex]}_${Date.now()}_${file.name}`);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      const updatedImages = [...progressImages];
      updatedImages[stageIndex] = downloadURL;
      setProgressImages(updatedImages);
      handleSaveProgress(progressDetails, updatedImages);
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  const handleScrollNews = (direction) => {
    if (direction === 'left' && canScrollLeft) {
      setNewsStartIndex(Math.max(0, newsStartIndex - postsPerPage));
    } else if (direction === 'right' && canScrollRight) {
      setNewsStartIndex(Math.min(posts.length - postsPerPage, newsStartIndex + postsPerPage));
    }
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    if (!user || !newPost.title.trim() || !newPost.content.trim()) return;

    setPosting(true);
    setPostError(null);

    try {
      if (editingPost) {
        // Update existing post
        const postRef = doc(db, 'posts', editingPost.id);
        await updateDoc(postRef, {
          title: newPost.title.trim(),
          content: newPost.content.trim(),
          image: newPost.image.trim() || null,
        });
        setEditingPost(null);
      } else {
        // Create new post
        await addDoc(collection(db, 'posts'), {
          title: newPost.title.trim(),
          content: newPost.content.trim(),
          image: newPost.image.trim() || null,
          time: new Date(),
          agree: 0,
          disagree: 0
        });
      }
      setNewPost({ title: '', content: '', image: '' });
    } catch (error) {
      setPostError('Failed to post. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setNewPost({
      title: post.title,
      content: post.content,
      image: post.image || ''
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingPost(null);
    setNewPost({ title: '', content: '', image: '' });
  };

  const handleVote = async (postId, voteType) => {
    if (sessionVotes[postId]) return;

    const postRef = doc(db, 'posts', postId);
    try {
      await updateDoc(postRef, { [voteType]: increment(1) });
      setSessionVotes(prev => ({ ...prev, [postId]: voteType }));
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    try {
      await addDoc(collection(db, 'feedback'), {
        text: feedback.trim(),
        time: new Date(),
        agree: 0
      });
      setFeedback('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleFeedbackAgree = async (feedbackId) => {
    if (sessionFeedbackAgrees[feedbackId]) return;

    const fbRef = doc(db, 'feedback', feedbackId);
    try {
      await updateDoc(fbRef, { agree: increment(1) });
      setSessionFeedbackAgrees(prev => ({ ...prev, [feedbackId]: true }));
    } catch (error) {
      console.error('Error agreeing:', error);
    }
  };

  const handleReplyChange = (feedbackId, value) => {
    setReplyInputs(prev => ({ ...prev, [feedbackId]: value }));
  };

  const handleReplySubmit = async (feedbackId) => {
    if (!user || !replyInputs[feedbackId]?.trim()) return;

    setReplyLoading(prev => ({ ...prev, [feedbackId]: true }));

    try {
      const fbRef = doc(db, 'feedback', feedbackId);
      await updateDoc(fbRef, { reply: replyInputs[feedbackId].trim() });
      setReplyInputs(prev => ({ ...prev, [feedbackId]: '' }));
    } catch (error) {
      console.error('Error replying:', error);
    } finally {
      setReplyLoading(prev => ({ ...prev, [feedbackId]: false }));
    }
  };

  const formatTime = (time) => {
    if (!time) return 'Unknown time';
    const date = time.toDate ? time.toDate() : new Date(time);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const truncateText = (text, maxLength) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "final-feedback":
        return <FinalFeedbackTab user={user} />;

      case "overview":
        return (
          <OverviewTab
            supporters={supporters}
            hasSupported={hasSupported}
            supporting={supporting}
            showThanks={showThanks}
            handleSupport={handleSupport}
            setActiveTab={setActiveTab}
            STAGES={STAGES}
            progressIndex={progressIndex}
            progressDetails={progressDetails}
            progressImages={progressImages}
            posts={posts}
            setSelectedPost={setSelectedPost}
            formatTime={formatTime}
          />
        );

      case "progress":
        return (
          <ProgressTab
            supporters={supporters}
            hasSupported={hasSupported}
            supporting={supporting}
            showThanks={showThanks}
            handleSupport={handleSupport}
            STAGES={STAGES}
            progressIndex={progressIndex}
            progressDetails={progressDetails}
            progressImages={progressImages}
            setProgressDetails={setProgressDetails}
            setProgressImages={setProgressImages}
            handleProgressIndexChange={handleProgressIndexChange}
            handleSaveProgress={handleSaveProgress}
            handleStageImageFile={handleStageImageFile}
            user={user}
          />
        );

      case "plan-details":
        return <PlanDetailsTab />;

      case "milestones":
        return <MilestonesTab />;

      case "faq":
        return <FAQTab />;

      case "surveys":
        return (
          <SurveysTab
            popularConcerns={popularConcerns}
            user={user}
            handleDeleteFeedback={handleDeleteFeedback}
            formatTime={formatTime}
            sessionFeedbackAgrees={sessionFeedbackAgrees}
            handleFeedbackAgree={handleFeedbackAgree}
            replyInputs={replyInputs}
            handleReplySubmit={handleReplySubmit}
            handleReplyChange={handleReplyChange}
            replyLoading={replyLoading}
            feedback={feedback}
            setFeedback={setFeedback}
            handleFeedbackSubmit={handleFeedbackSubmit}
          />
        );

      case "survey-results":
        return <SurveyResultsTab />;

      case "visuals":
        return <VisualsTab />;

      case "resources":
        return <ResourcesTab />;

      case "news":
        return (
          <NewsTab
            user={user}
            newPost={newPost}
            setNewPost={setNewPost}
            handlePostSubmit={handlePostSubmit}
            editingPost={editingPost}
            handleCancelEdit={handleCancelEdit}
            postError={postError}
            posting={posting}
            newsStartIndex={newsStartIndex}
            postsPerPage={postsPerPage}
            posts={posts}
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
            handleScrollNews={handleScrollNews}
            latestNews={latestNews}
            setSelectedPost={setSelectedPost}
            handleDeletePost={handleDeletePost}
            handleEditPost={handleEditPost}
            formatTime={formatTime}
            truncateText={truncateText}
            sessionVotes={sessionVotes}
            handleVote={handleVote}
          />
        );

      default:
        return <div>Select a section from the sidebar</div>;
    }
  };

  return (
    <SidebarProvider>
      <div className="flex h-full w-full pt-16">
        <Sidebar className="w-64 bg-black/40 backdrop-blur-sm border-r border-secondary/10">
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel className="flex items-center gap-2 px-2 py-2 text-secondary/80">
                <Trees className="h-5 w-5" />
                Miner's Park Master Plan
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isTabActive = activeTab === tab.id;
                    return (
                      <SidebarMenuItem key={tab.id}>
                        <SidebarMenuButton
                          onClick={() => {
                            setActiveTab(tab.id);
                            if (isMobile) {
                              setOpenMobile(false);
                            }
                            // Scroll to top of page smoothly
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          isActive={isTabActive}
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-2 transition-all rounded-md",
                            isTabActive
                              ? "bg-secondary text-black font-semibold hover:bg-secondary/90"
                              : "text-gray-400 hover:text-gray-200 hover:bg-white/5",
                            tab.highlight && !isTabActive && "text-secondary font-medium animate-pulse"
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          {tab.label}
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>

        <main className="flex-1 p-6 overflow-y-auto relative">
          <div className="md:hidden fixed top-20 left-4 z-50">
            <SidebarTrigger className="text-secondary" />
          </div>
          <div className="md:hidden mb-4"></div>
          {renderTabContent()}
        </main>
      </div>

      {/* Post Detail Modal */}
      {selectedPost && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedPost(null)}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedPost.image && (
              <div className="modal-image">
                <img
                  src={selectedPost.image}
                  alt={selectedPost.title}
                  className="modal-image-img"
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
              </div>
            )}
            <div className="modal-body">
              <div className="modal-header">
                <div>
                  <h3 className="modal-title">{selectedPost.title}</h3>
                  <div className="text-sm text-gray-500">{formatTime(selectedPost.time)}</div>
                </div>
                <button
                  className="modal-close"
                  onClick={() => setSelectedPost(null)}
                  aria-label="Close"
                >
                  ✖
                </button>
              </div>
              <div className="modal-text">
                {selectedPost.content}
              </div>
              <div className="modal-actions">
                <button
                  className={`btn-vote flex items-center ${sessionVotes[selectedPost.id] === 'agree' ? 'btn-vote-active-agree' : ''}`}
                  onClick={() => handleVote(selectedPost.id, 'agree')}
                  title="Agree"
                >
                  <span className="mr-2" role="img" aria-label="thumbs up">👍</span>
                  {selectedPost.agree || 0} Agree
                </button>
                <button
                  className={`btn-vote flex items-center ${sessionVotes[selectedPost.id] === 'disagree' ? 'btn-vote-active-disagree' : ''}`}
                  onClick={() => handleVote(selectedPost.id, 'disagree')}
                  title="Disagree"
                >
                  <span className="mr-2" role="img" aria-label="thumbs down">👎</span>
                  {selectedPost.disagree || 0} Disagree
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SidebarProvider>
  );
}

function Dashboard() {
  return (
    <SidebarProvider>
      <DashboardContent />
    </SidebarProvider>
  );
}

export default Dashboard;
